import React, { useState, useEffect, forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';

const inputVariants = {
  base: 'flex h-12 w-full rounded-sm border border-black border-2 bg-background px-3 py-2 text-md ring-offset-background file:border-0 file:bg-transparent file:text-md file:font-medium placeholder:text-muted-foreground focus-visible:border-transparent focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50',
};

const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => {
    return <input ref={ref} type={type} className={cn(inputVariants.base, className)} {...props} />;
  },
);
Input.displayName = 'Input';

const PasswordInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => {
    return <Input ref={ref} type="password" className={cn(inputVariants.base, className)} {...props} />;
  },
);
PasswordInput.displayName = 'PasswordInput';

const MIN_AGE = 18;
const MIN_YEAR = 1930;

interface DateInputProps {
  id?: string;
  autoFocus?: boolean;
  register: any;
  unregister: any;
  watch: any;
  setValue: any;
  required?: boolean;
  tabIndex?: number;
  className?: string;
  /** Reveal the error even if the field was never focused, e.g. after a failed submit */
  showError?: boolean;
}

const DateInput = ({
  id,
  autoFocus,
  register,
  unregister,
  watch,
  setValue,
  required = false,
  tabIndex,
  className,
  showError = false,
}: DateInputProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  const minAge = dayjs().subtract(MIN_AGE, 'years');
  const maxAge = dayjs(`${MIN_YEAR}-01-01`);
  const watchedDob = watch('dob', null);

  const validateAge = (dateString: string) => {
    if (!dateString && required) {
      return 'Date of birth is required';
    }

    if (!dateString) return null;

    // Parse MM/DD/YYYY format
    const parts = dateString.split('/');
    if (parts.length !== 3) return 'Invalid date format';

    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(month) || isNaN(day) || isNaN(year)) {
      return 'Invalid date format';
    }

    const date = dayjs(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`);

    if (!date.isValid()) {
      return 'Invalid date';
    }

    if (date.isAfter(minAge)) {
      return `Must be at least ${MIN_AGE} years old`;
    }

    if (date.isBefore(maxAge)) {
      return `Date cannot be earlier than ${MIN_YEAR}`;
    }

    return null;
  };

  useEffect(() => {
    register('dob', {
      validate: (value: string) => {
        const validationError = validateAge(value);
        setError(validationError);
        return validationError || true;
      },
    });

    return () => {
      unregister('dob');
    };
  }, [register, unregister, required]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = e.target.value;
    setValue('dob', dateString, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const validationError = validateAge(dateString);
    setError(validationError);
  };

  const formatInputValue = (value: string) => {
    if (!value) return '';

    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as MM/DD/YYYY
    if (digits.length >= 8) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    } else if (digits.length >= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length >= 2) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    return digits;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTyping(true);
    const formatted = formatInputValue(e.target.value);
    e.target.value = formatted;
    handleDateChange(e);
  };

  const handleBlur = () => {
    setIsTyping(false);
    setHasBlurred(true);
  };

  const isErrorVisible = Boolean(error) && !isTyping && (hasBlurred || showError);

  return (
    <div className={cn('w-full relative', className)} data-sentry-mask>
      <div className="relative">
        <Input
          id={id}
          type="text"
          autoFocus={autoFocus}
          tabIndex={tabIndex}
          placeholder="MM/DD/YYYY"
          value={watchedDob || ''}
          onChange={handleInputChange}
          onBlur={handleBlur}
          className={cn(inputVariants.base, isErrorVisible ? 'border-red-500 focus-visible:ring-red-500' : '')}
          maxLength={10}
        />
      </div>
      {isErrorVisible && <div className="text-red-500 text-xs mt-1 ml-3">{error}</div>}
    </div>
  );
};

export { Input, PasswordInput, DateInput, inputVariants };
